import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageDown, Loader2 } from "lucide-react";
import { Input } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

type RC = { running: boolean; done: number; total: number; changed: number; failed: number; saved_bytes: number; finished_at?: string };
type Lim = { max_kb: number; max_side: number; files: number; total_bytes: number; over_limit: number; recompress?: RC | null };
const mb = (b: number) => (b / 1e6).toFixed(b > 1e7 ? 0 : 1);

export const ImageLimitsSettings = ({ canEdit }: { canEdit: boolean }) => {
  const { t } = usePrefs();
  const [d, setD] = useState<Lim | null>(null);
  const [f, setF] = useState({ max_kb: "", max_side: "" });
  const load = () => api.get("/admin/image-limits").then((r) => { setD(r.data); setF((x) => x.max_kb ? x : { max_kb: String(r.data.max_kb), max_side: String(r.data.max_side) }); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const running = !!d?.recompress?.running;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [running]);

  const save = async () => {
    try {
      const { data } = await api.put("/admin/image-limits", { max_kb: Number(f.max_kb), max_side: Number(f.max_side) });
      setD(data); toast.success(t("img.saved"));
    } catch (e) { toast.error(errorText(e, t)); }
  };
  const start = async () => {
    if (!window.confirm(t("img.confirm").replace("{kb}", String(d?.max_kb)))) return;
    try { await api.post("/admin/image-limits/recompress"); toast.success(t("img.started")); setTimeout(load, 800); } catch (e) { toast.error(errorText(e, t)); }
  };
  if (!d) return null;
  const rc = d.recompress;
  const num = (k: "max_kb" | "max_side", label: string, hint: string) => (
    <label className="text-xs font-bold space-y-1">{label}
      <Input dir="ltr" inputMode="numeric" value={f[k]} disabled={!canEdit} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value.replace(/\D/g, "") })} data-testid={`image-limit-${k}`} className="h-10 rounded-full w-32" />
      <span className="block font-normal text-muted-foreground">{hint}</span>
    </label>
  );

  return (
    <div data-testid="image-limits-card" className="rounded-3xl border bg-card p-5 space-y-4">
      <div>
        <p className="font-bold flex items-center gap-2"><ImageDown className="h-5 w-5 text-primary" />{t("img.title")}</p>
        <p className="text-sm text-muted-foreground">{t("img.note")}</p>
      </div>
      <div className="flex flex-wrap items-start gap-3">
        {num("max_kb", t("img.maxKb"), "30 – 2000")}
        {num("max_side", t("img.maxSide"), "480 – 4096")}
        {canEdit && <button onClick={save} data-testid="image-limits-save" className="h-10 mt-5 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold">{t("common.save")}</button>}
      </div>
      <p data-testid="image-limits-stats" className="text-xs text-muted-foreground">
        {t("img.stats").replace("{n}", String(d.files)).replace("{mb}", mb(d.total_bytes)).replace("{over}", String(d.over_limit))}
      </p>
      {canEdit && (
        <div className="rounded-2xl bg-accent/60 p-4 space-y-2">
          <button onClick={start} disabled={running} data-testid="image-recompress-button" className="h-10 px-5 rounded-full border bg-background text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60">
            {running && <Loader2 className="h-4 w-4 animate-spin" />}{t(running ? "img.running" : "img.recompress")}
          </button>
          {rc && (
            <div data-testid="image-recompress-status" className="text-xs space-y-1">
              {running && <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-[width]" style={{ width: `${rc.total ? (rc.done / rc.total) * 100 : 0}%` }} /></div>}
              <p>{t("img.progress").replace("{done}", String(rc.done)).replace("{total}", String(rc.total)).replace("{changed}", String(rc.changed)).replace("{mb}", mb(rc.saved_bytes))}{rc.failed ? ` · ${t("img.failed").replace("{n}", String(rc.failed))}` : ""}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
